import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Clock, ShieldCheck, Star, UserPlus } from "lucide-react";
import { BookButton, CallButton, EmergencyNote } from "@/components/CTA";

const LINES = [
  { text: "Primary Care", accent: false },
  { text: "That Fits", accent: false },
  { text: "Your Life.", accent: true },
];

const TRUST_POINTS = [
  { icon: UserPlus, label: "Now Accepting New Patients", testid: "trust-point-new-patients" },
  { icon: Clock, label: "Evening & Weekend Appointments", testid: "trust-point-evening-weekend" },
  { icon: ShieldCheck, label: "Military Family-Owned & Operated", testid: "trust-point-military" },
  { icon: Star, label: "5-Star Patient Rating", testid: "trust-point-rating" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden"
    >
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-20 pt-14 sm:px-6 md:pt-20 lg:grid-cols-12 lg:gap-10 lg:px-12 lg:pb-28">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center gap-3"
          >
            <span className="h-2 w-2 animate-pulse-dot rounded-full bg-brand-red" />
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">
              Prescott Valley, AZ · Military Family-Owned
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-brand-navy sm:text-5xl lg:text-6xl"
          >
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.accent ? "font-serif italic text-brand-red" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{
                    duration: 0.95,
                    delay: 0.15 + i * 0.13,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subcopy"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            Compassionate, personalized care for individuals and families in
            Prescott Valley—with evening and weekend appointments designed
            around your schedule.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8"
          >
            <div className="flex flex-wrap items-center gap-4">
              <BookButton testid="hero-book-online-btn" />
              <CallButton testid="hero-call-btn" label="Call for Same-Day Care" />
            </div>
            <EmergencyNote testid="hero-emergency-note" className="mt-5 max-w-md" />
          </motion.div>

          <motion.ul
            data-testid="hero-trust-points"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-12 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2"
          >
            {TRUST_POINTS.map((point, i) => (
              <motion.li
                key={point.testid}
                data-testid={point.testid}
                initial={{ opacity: 0, x: -14 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 1 + i * 0.1 }}
                className="flex items-center gap-3"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-redlight">
                  <point.icon className="h-4 w-4 text-brand-red" />
                </span>
                <span className="text-sm font-medium text-slate-700">{point.label}</span>
              </motion.li>
            ))}
          </motion.ul>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-3 rotate-2 rounded-[2.5rem] bg-brand-redlight" />
            <div className="absolute -inset-3 -rotate-1 rounded-[2.5rem] border border-slate-200" />
            <div className="relative overflow-hidden rounded-[2rem] shadow-lift">
              <motion.img
                style={{ y }}
                src="/images/jeremy-exam-wide.webp"
                alt="Jeremy Pene, FNP-C examining a patient at Advantage Primary Health in Prescott Valley"
                data-testid="hero-image"
                className="h-[400px] w-full scale-110 object-cover lg:h-[540px]"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.2 }}
              data-testid="hero-rating-chip"
              className="absolute -left-3 bottom-8 rounded-2xl bg-white/95 px-5 py-4 shadow-lift backdrop-blur sm:-left-6"
            >
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="mt-1.5 text-xs font-semibold text-brand-navy">
                5.0 Patient Rating
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.35 }}
              data-testid="hero-new-patients-chip"
              className="absolute -right-2 top-8 rounded-full bg-brand-navy px-5 py-2.5 shadow-lift sm:-right-4"
            >
              <p className="text-xs font-semibold text-white">
                Now Accepting New Patients
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
